import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { FileText, Plus, Trash2, Download, Pencil } from "lucide-react";

const STATUSES = ["bozza", "inviato", "accettato", "rifiutato", "pagato"];
const emptyLine = () => ({ description: "", quantity: 1, unit_price: 0, vat_percent: 22 });
const emptyForm = () => ({
  kind: "preventivo",
  client_name: "",
  client_vat: "",
  client_fiscal_code: "",
  client_address: "",
  client_email: "",
  technician_name: "",
  date: new Date().toISOString().slice(0, 10),
  valid_until: "",
  lines: [emptyLine()],
  discount_percent: 0,
  apply_iva: true,
  notes: "",
  status: "bozza",
});

export default function PreventiviPage({ kind = "preventivo" }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/documents", { params: { kind } });
      setDocs(data);
    } catch (e) { toast.error("Errore caricamento"); }
  };
  useEffect(() => { if (!user || user.role === "admin") load(); /* eslint-disable-next-line */ }, [kind, user]);

  const totals = useMemo(() => {
    const sub = form.lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
    const disc = sub * (Number(form.discount_percent) || 0) / 100;
    const net = sub - disc;
    let vat = 0;
    if (form.apply_iva && sub > 0) {
      const w = form.lines.reduce((v, l) => {
        const ln = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
        return v + (ln / sub) * (Number(l.vat_percent) || 0);
      }, 0);
      vat = net * w / 100;
    }
    return { sub, disc, net, vat, total: net + vat };
  }, [form.lines, form.discount_percent, form.apply_iva]);

  if (user && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm(), kind }); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      kind: d.kind,
      client_name: d.client_name || "", client_vat: d.client_vat || "", client_fiscal_code: d.client_fiscal_code || "",
      client_address: d.client_address || "", client_email: d.client_email || "", technician_name: d.technician_name || "",
      date: d.date || new Date().toISOString().slice(0, 10),
      valid_until: d.valid_until || "",
      lines: (d.lines && d.lines.length ? d.lines : [emptyLine()]).map((l) => ({ ...l })),
      discount_percent: d.discount_percent || 0,
      apply_iva: d.apply_iva !== false,
      notes: d.notes || "",
      status: d.status || "bozza",
    });
    setOpen(true);
  };

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const changeLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));

  const save = async () => {
    if (!form.client_name.trim()) return toast.error(t("auto.nome_cliente_richiesto"));
    if (form.lines.length === 0 || !form.lines.some((l) => l.description && Number(l.quantity) > 0)) {
      return toast.error(t("auto.aggiungi_almeno_una_riga_valida"));
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_percent: Number(form.discount_percent) || 0,
        lines: form.lines.filter((l) => l.description).map((l) => ({
          description: l.description, quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0, vat_percent: Number(l.vat_percent) || 0,
        })),
      };
      if (editing) await api.patch(`/documents/${editing.id}`, payload);
      else await api.post("/documents", payload);
      toast.success(editing ? "Aggiornato" : "Creato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success(t("common.deleted")); load(); }
    catch (e) { toast.error(t("common.error")); }
  };

  const downloadPdf = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${d.kind}_${d.number || d.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore PDF"); }
  };

  const titleSingular = kind === "preventivo" ? "Preventivo" : "BEF manuale";
  const titlePlural = kind === "preventivo" ? "Preventivi" : "BEF manuali";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.documenti_amministrativi_kicker")}</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{titlePlural}</h1>
          <div className="mt-1 text-sm text-slate-500">{kind === "preventivo" ? t("auto.crea_liberi_preventivo_hint") : t("auto.crea_liberi_bef_hint")}</div>
        </div>
        <Button onClick={openCreate} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="doc-new-btn">
          <Plus className="mr-2 h-4 w-4" /> {kind === "preventivo" ? t("auto.nuovo_preventivo_btn") : t("auto.nuovo_bef_manuale_btn")}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">{t("auto.numero")}</th>
              <th className="p-3">{t("common.date")}</th>
              <th className="p-3">{t("common.customer")}</th>
              <th className="p-3 text-right">{t("common.total")}</th>
              <th className="p-3">{t("common.status")}</th>
              <th className="p-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">{t("auto.nessun_documento_placeholder", { kind: titleSingular.toLowerCase(), label: titleSingular })}</td></tr>}
            {docs.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`doc-row-${d.id}`}>
                <td className="p-3 font-mono font-semibold">{d.number}</td>
                <td className="p-3 font-mono text-xs">{d.date}</td>
                <td className="p-3">
                  <div className="font-semibold">{d.client_name}</div>
                  {d.client_vat && <div className="text-xs text-slate-500">P.IVA {d.client_vat}</div>}
                </td>
                <td className="p-3 text-right font-mono font-semibold">€ {Number(d.total || 0).toFixed(2)}</td>
                <td className="p-3">
                  <span className="rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-700">{d.status}</span>
                </td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(d)} data-testid={`doc-pdf-${d.id}`}><Download className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`doc-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`doc-del-${d.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>{t("auto.eliminare_documento_q", { number: d.number })}</AlertDialogTitle><AlertDialogDescription>{t("auto.operazione_irreversibile")}</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel><AlertDialogAction onClick={() => del(d.id)} className="bg-red-600 hover:bg-red-700">{t("common.delete")}</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? (kind === "preventivo" ? t("auto.modifica_preventivo") : t("auto.modifica_bef_manuale")) : (kind === "preventivo" ? t("auto.nuovo_preventivo_btn") : t("auto.nuovo_bef_manuale_btn"))}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div><Label>{t("common.date")}</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm" /></div>
              {kind === "preventivo" && <div><Label>{t("auto.valido_fino_al")}</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="rounded-sm" data-testid="doc-valid-until" /></div>}
              <div><Label>{t("auto.stato_label")}</Label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full rounded-sm border border-slate-200 p-2 text-sm" data-testid="doc-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><Label>{t("auto.sconto")}</Label><Input type="number" step="0.01" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="rounded-sm font-mono" data-testid="doc-discount" /></div>
            </div>

            <div className="rounded-sm border border-slate-200 p-3">
              <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.cliente")}</div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div><Label>{t("auto.ragione_sociale_req")}</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="rounded-sm" data-testid="doc-client-name" /></div>
                <div><Label>{t("auto.piva")}</Label><Input value={form.client_vat} onChange={(e) => setForm({ ...form, client_vat: e.target.value })} className="rounded-sm font-mono" /></div>
                <div><Label>{t("auto.codice_fiscale_label")}</Label><Input value={form.client_fiscal_code} onChange={(e) => setForm({ ...form, client_fiscal_code: e.target.value })} className="rounded-sm font-mono" /></div>
                <div><Label>{t("common.email")}</Label><Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="rounded-sm" /></div>
                <div className="md:col-span-2"><Label>{t("common.address")}</Label><Input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} className="rounded-sm" /></div>
                {kind === "bef" && <div className="md:col-span-2"><Label>{t("auto.nome_tecnico_libero")}</Label><Input value={form.technician_name} onChange={(e) => setForm({ ...form, technician_name: e.target.value })} className="rounded-sm" placeholder={t("auto.anche_non_registrato_in_azienda")} data-testid="doc-tech-name" /></div>}
              </div>
            </div>

            <div className="rounded-sm border border-slate-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.righe_count")} ({form.lines.length})</div>
                <Button size="sm" variant="outline" onClick={addLine} className="h-7 rounded-sm text-xs" data-testid="doc-add-line"><Plus className="mr-1 h-3 w-3" /> {t("auto.riga_short")}</Button>
              </div>
              <div className="space-y-2">
                {form.lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2" data-testid={`doc-line-${i}`}>
                    <div className="col-span-6"><Input value={l.description} onChange={(e) => changeLine(i, { description: e.target.value })} placeholder={t("auto.descrizione_lav_prodotto_ph")} className="h-8 rounded-sm text-xs" data-testid={`doc-line-desc-${i}`} /></div>
                    <div className="col-span-1"><Input type="number" step="0.01" value={l.quantity} onChange={(e) => changeLine(i, { quantity: e.target.value })} placeholder={t("auto.col_qt")} className="h-8 rounded-sm font-mono text-xs" data-testid={`doc-line-qty-${i}`} /></div>
                    <div className="col-span-2"><Input type="number" step="0.01" value={l.unit_price} onChange={(e) => changeLine(i, { unit_price: e.target.value })} placeholder={t("auto.col_eur_u")} className="h-8 rounded-sm font-mono text-xs" data-testid={`doc-line-price-${i}`} /></div>
                    <div className="col-span-2"><Input type="number" step="0.01" value={l.vat_percent} onChange={(e) => changeLine(i, { vat_percent: e.target.value })} placeholder={t("auto.iva")} className="h-8 rounded-sm font-mono text-xs" /></div>
                    <div className="col-span-1 flex items-center justify-end"><Button size="sm" variant="ghost" onClick={() => removeLine(i)} className="h-8 w-8 p-0"><Trash2 className="h-4 w-4 text-red-600" /></Button></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.apply_iva} onChange={(e) => setForm({ ...form, apply_iva: e.target.checked })} className="h-4 w-4" data-testid="doc-apply-iva" />
                  <span>{t("auto.applica_iva")}</span>
                </label>
                <div className="mt-2"><Label>{t("common.notes")}</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-sm text-sm" data-testid="doc-notes" /></div>
              </div>
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm">
                <div className="flex justify-between"><span>{t("auto.imponibile")}</span><span className="font-mono">€ {totals.sub.toFixed(2)}</span></div>
                <div className="flex justify-between text-slate-500"><span>{t("auto.sconto_percent_label", { percent: form.discount_percent })}</span><span className="font-mono">- € {totals.disc.toFixed(2)}</span></div>
                <div className="flex justify-between border-t border-slate-200 pt-1"><span>{t("auto.netto_label")}</span><span className="font-mono">€ {totals.net.toFixed(2)}</span></div>
                {form.apply_iva && <div className="flex justify-between"><span>{t("auto.iva_x")}</span><span className="font-mono">€ {totals.vat.toFixed(2)}</span></div>}
                <div className="mt-2 flex justify-between border-t-2 border-slate-900 pt-1 text-base font-bold"><span>{t("auto.totale_x")}</span><span className="font-mono" data-testid="doc-total">€ {totals.total.toFixed(2)}</span></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="doc-save">{saving ? t("auto.salvataggio_ellipsis") : t("auto.salva_btn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
